import { NavLink } from "react-router-dom";
import SectionHeader from "../components/SectionHeader";
import TestimonialCard from "../components/TestimonialCard";
import { routePaths } from "../constants/routes";
import "../style/about.css";
import qaCoderLogo from "../assets/qacoder-logo.svg";

const audienceCards = [
  {
    title: "Freshers",
    description: "Start with QA basics, interview language, test cases, defects, and real project understanding.",
  },
  {
    title: "Manual Testers",
    description: "Move from manual execution into automation thinking, locators, waits, frameworks, and APIs.",
  },
  {
    title: "Career Switchers",
    description: "Follow a beginner-safe route into software testing with practical examples and mentor support.",
  },
];

const principles = [
  "Teach concepts with practical QA examples, not only definitions.",
  "Connect every topic to interviews, projects, or workplace testing decisions.",
  "Give learners free practice before asking them to join a paid course.",
  "Build confidence through repetition, assignments, and structured revision.",
];

const outcomes = [
  { value: "Manual QA", label: "Test cases, bugs, SDLC, STLC, Jira basics" },
  { value: "Automation", label: "Selenium, Playwright thinking, locators, waits" },
  { value: "API QA", label: "Requests, responses, status codes, validation" },
  { value: "Career Prep", label: "Resume, mock interviews, project explanation" },
];

const mentorHighlights = [
  "Lead Engineer with hands-on QA automation experience across web, API, mobile, and data validation projects.",
  "Built and maintained automation frameworks using Selenium, Playwright, Cypress, Rest Assured, Postman, Newman, Jenkins, Git, and Azure DevOps.",
  "Worked across logistics, social media/newsletter, music learning, ed-tech, fintech, and transport service domains.",
  "Focuses on practical QA training: test design, automation strategy, CI/CD, defect analysis, and interview-ready project explanation.",
];

const mentorStats = [
  { value: "Lead Engineer", label: "Trainer position" },
  { value: "5+ domains", label: "Project exposure" },
  { value: "40%", label: "Automation efficiency impact" },
  { value: "CI/CD", label: "Jenkins and Git workflows" },
];

const toolStack = [
  "Selenium",
  "Playwright",
  "Cypress",
  "Postman",
  "Rest Assured",
  "Jenkins",
  "Azure DevOps",
  "Appium",
  "TestNG",
  "Jira",
];

export default function AboutUs() {
  return (
    <main className="about-page">
      <section className="about-hero">
        <div className="about-hero-copy">
          <span className="ui-badge">About QA Coder</span>
          <h1>We help learners become practical, interview-ready QA professionals.</h1>
          <p>
            QA Coder is a software testing learning platform focused on Manual Testing,
            Automation Testing, API Testing, interview preparation, and hands-on practice.
            The goal is simple: make QA learning clear, practical, and career-focused.
          </p>
          <div className="about-hero-actions">
            <NavLink className="btn-ui" to={routePaths.automationTesting}>
              Start Free Practice
            </NavLink>
            <NavLink className="btn-ui-secondary" to={routePaths.enroll}>
              Join Live Course
            </NavLink>
          </div>
        </div>

        <div className="about-founder-card surface-card">
          <div className="about-brand-mark">
            <img src={qaCoderLogo} alt="QA Coder logo" />
          </div>
          <div className="about-founder-copy">
            <span>Founder, Trainer and Lead Engineer</span>
            <h2>Manish Prajapati</h2>
            <p>
              Lead Engineer in QA Automation and testing mentor helping learners
              build practical confidence through real projects, automation frameworks,
              API validation, CI/CD workflows, and interview guidance.
            </p>
          </div>
          <div className="about-founder-stats">
            {mentorStats.map((item) => (
              <div key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-mentor-section surface-card">
        <div>
          <span className="ui-badge">Trainer Profile</span>
          <h2>Learning from real QA engineering experience, not only classroom theory.</h2>
          <p>
            The QA Coder training approach is shaped by hands-on experience in
            automation frameworks, API testing, mobile testing, CI/CD execution,
            defect management, Agile delivery, and cross-functional collaboration.
          </p>
        </div>
        <div className="about-mentor-content">
          <ul className="about-highlight-list">
            {mentorHighlights.map((highlight) => (
              <li key={highlight}>{highlight}</li>
            ))}
          </ul>
          <div className="about-tool-stack" aria-label="Trainer tools and skills">
            {toolStack.map((tool) => (
              <span key={tool}>{tool}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section">
        <SectionHeader
          eyebrow="Who We Help"
          title="Built for learners who want a real QA career path."
          description="The platform supports multiple starting points, from complete beginners to testers moving into automation."
        />
        <div className="about-card-grid">
          {audienceCards.map((card) => (
            <article className="about-info-card surface-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section about-method-section">
        <div>
          <span className="ui-badge">Teaching Philosophy</span>
          <h2>Practice first, then explain with confidence.</h2>
          <p>
            QA Coder is designed around the way testers actually grow: understand the
            concept, practice the behavior, write test cases, then prepare to explain it
            in interviews and real projects.
          </p>
        </div>
        <ul className="about-principle-list">
          {principles.map((principle) => (
            <li key={principle}>{principle}</li>
          ))}
        </ul>
      </section>

      <section className="about-section">
        <SectionHeader
          eyebrow="Learning Outcomes"
          title="What learners should be able to do after following QA Coder."
        />
        <div className="about-outcome-grid">
          {outcomes.map((item) => (
            <div className="about-outcome-card surface-card" key={item.value}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="about-section">
        <SectionHeader
          eyebrow="Learner Voice"
          title="The experience we want every learner to feel."
        />
        <div className="about-testimonial-grid">
          <TestimonialCard
            name="QA Coder Learner"
            role="Beginner QA Track"
            rating="5"
            quote="The roadmap helped me understand what to learn first and how to connect theory with practice."
            result="Clearer QA direction"
          />
          <TestimonialCard
            name="Automation Learner"
            role="Practice Lab User"
            rating="5"
            quote="The automation practice modules made locators, alerts, forms, and widgets easier to understand."
            result="Better hands-on confidence"
          />
        </div>
      </section>

      <section className="about-final-cta surface-card">
        <div>
          <span className="ui-badge">Start Today</span>
          <h2>Learn free, practice live, and join the course when you want mentor support.</h2>
        </div>
        <div className="about-final-actions">
          <NavLink className="btn-ui" to={routePaths.studyNotes}>
            Explore Study Notes
          </NavLink>
          <NavLink className="btn-ui-secondary" to={routePaths.contact}>
            Talk to Mentor
          </NavLink>
        </div>
      </section>
    </main>
  );
}
